/**
 * Produmax ASN Export section.
 *
 * Toggles the `asn_produmax_export` feature flag, which adds a "Produmax Export"
 * button to the ASN detail modal (see {@link ASNDetailModal}). The flag lives
 * inside PlatformSettings.feature_flags; the whole dict is round-tripped on save
 * so sibling flags are preserved.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, Download } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

export function ProdumaxExportSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  // The full feature_flags dict is kept so a toggle save doesn't drop siblings.
  const [featureFlags, setFeatureFlags] = useState<Record<string, unknown>>({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const flags = data.feature_flags ?? {};
        setFeatureFlags(flags);
        setEnabled(!!flags.asn_produmax_export);
      } catch (err: unknown) {
        console.error('Error loading Produmax Export setting:', err);
        toast.error('Failed to load Produmax Export setting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const nextFlags = { ...featureFlags, asn_produmax_export: checked };
      await platformSettingsAPI.patchSettings({ feature_flags: nextFlags });
      setFeatureFlags(nextFlags);
      toast.success(`Produmax ASN Export: ${checked ? 'ON' : 'OFF'}`);
    } catch (err: unknown) {
      console.error('Error saving Produmax Export setting:', err);
      toast.error('Failed to save Produmax Export setting');
      setEnabled(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Produmax ASN Export
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Adds a "Produmax Export" button to the ASN detail modal that downloads the
          ASN lines as a DocNum,LineNum,Quantity CSV for the Produmax ERP.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="space-y-1 pr-3">
            <Label htmlFor="asn-produmax-export" className="text-sm font-medium">
              Enable Produmax ASN Export
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled
                ? 'ON — the Produmax Export button is shown on the ASN detail modal.'
                : 'OFF — the button is hidden.'}
            </p>
          </div>
          <Switch
            id="asn-produmax-export"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
          />
        </div>
      </CardContent>
    </Card>
  );
}
